import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { MapPin } from 'lucide-react';
import { ReactComponent as ButterflyIcon } from 'assets/images/common/logo-butterfly.svg';
import ReactDOM from 'react-dom';
import EmojiAvatar from '../../homePage/EmojiAvatar';
import { useAuth } from 'modules/auth/context/AuthContextV2';
import {
  PlaceRecommendation,
  Prompt,
  SelectOption,
  InvitePreview,
  Message,
} from './types';
import { mockPrompts } from './mocks';
import { useSendChatMessageMutation } from 'modules/home/services/ai.service';
import { SquareAutocompleteInput } from 'components/v2/squareAutocompleteInput/SquareAutocompleteInput';
import Button from 'components/v2/button/Button';
import { PlacePrediction } from 'components/v2/squareLocationInput/SquareLocationInput';
import { cn } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';

const PromptButton: React.FC<{
  prompt: Prompt;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  chatHistory: Message[];
  setChatHistory: React.Dispatch<React.SetStateAction<Message[]>>;
  setIsAiTyping: (isAiTyping: boolean) => void;
}> = ({
  prompt,
  isOpen,
  setIsOpen,
  chatHistory,
  setChatHistory,
  setIsAiTyping,
}) => {
  const { user } = useAuth();
  const [sendChatMessage] = useSendChatMessageMutation();

  const handleSendMessage = async (message: string) => {
    if (!user) return;
    if (!isOpen) {
      setIsOpen(true);
    }

    const userMessage = {
      author: 'user',
      message: prompt.text,
    };
    setChatHistory((prev) => [...prev, userMessage]);
    localStorage.setItem(
      'chatHistory',
      JSON.stringify([...chatHistory, userMessage]),
    );

    try {
      setIsAiTyping(true);
      const response = await sendChatMessage({
        body: message,
        from: user.phoneNumber,
      });

      if (response.data) {
        const aiMessage = {
          author: 'ai',
          message: response.data.response,
        };
        setChatHistory((prev) => [...prev, aiMessage]);
        localStorage.setItem(
          'chatHistory',
          JSON.stringify([...chatHistory, userMessage, aiMessage]),
        );
        setIsAiTyping(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setIsAiTyping(false);
    }
  };

  return (
    <button
      className="bg-[#353555]/80 rounded-full py-2 px-4 flex items-center h-[40px] m-1"
      onClick={() => {
        handleSendMessage(prompt.text);
      }}
    >
      <span className="mr-2 text-xl">{prompt.icon}</span>
      <span className="text-white text-xs whitespace-nowrap">
        {prompt.text}
      </span>
    </button>
  );
};

const PlaceCard: React.FC<{
  place: PlaceRecommendation;
  variant: 'ai' | 'user';
  onClick?: () => void;
}> = ({ place, variant, onClick }) => {
  const isAi = variant === 'ai';

  return (
    <button
      className={`rounded-[16px] p-4 text-left w-full`}
      style={{
        background: isAi ? 'rgba(103, 80, 164, 0.7)' : '#32395E',
      }}
      onClick={onClick}
    >
      <div className={`${isAi ? 'text-right' : 'text-center'}`}>
        <h3 className="text-[#121417] font-medium text-base">{place.name}</h3>
        <div className="text-white text-sm">
          {place.rating && place.rating}
          {place.reviewCount && ` (${place.reviewCount})`}
          {place.category && ` • ${place.category}`}
          {place.distance && ` • ${place.distance}`}
        </div>
      </div>
    </button>
  );
};

const UserMessage: React.FC<{
  message: string;
  selectedPlace?: PlaceRecommendation;
}> = ({ message, selectedPlace }) => {
  return (
    <div className="flex flex-row-reverse items-start mb-5">
      {message && (
        <div
          className="bg-[#32395E] rounded-[1rem] py-3 px-4 max-w-[80%] break-words"
          style={{ boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.06)' }}
        >
          <p className="text-white text-[0.875rem] font-manrope">{message}</p>
        </div>
      )}
      {selectedPlace && (
        <div className="max-w-[80%] mt-2">
          <PlaceCard place={selectedPlace} variant="user" />
        </div>
      )}
    </div>
  );
};

const InvitePreviewCard: React.FC<{ preview: InvitePreview }> = ({
  preview,
}) => {
  const eventLink = preview.link?.split('/').pop();
  const navigate = useNavigate();

  return (
    <div
      className="w-full bg-[#6000E9] rounded-[16px] p-5 text-white"
      onClick={() => {
        navigate(`/lets-meet/view/${eventLink}`);
      }}
    >
      <h2 className="text-2xl font-bold mb-6">
        {`${preview.title} ${preview.date} at ${preview.time}?`}
      </h2>

      {preview.location && (
        <div className="flex items-center mb-3">
          <MapPin className="w-5 h-5 mr-2" />
          <span>{preview.location}</span>
        </div>
      )}

      {preview.attendees && preview.attendees.length > 0 && (
        <div className="flex items-center">
          <div className="w-5 h-5 mr-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                fill="white"
              />
            </svg>
          </div>
          <span>
            With {preview.attendees.filter((a) => a !== 'You').join(', ')}
          </span>
        </div>
      )}

      {preview.link && (
        <div className="mt-4 text-xs overflow-hidden text-ellipsis">
          {preview.link}
        </div>
      )}
    </div>
  );
};

const AIMessage: React.FC<{
  message: string;
  selectOptions?: SelectOption[];
  invitePreview?: InvitePreview;
  placeRecommendations?: PlaceRecommendation[];
  setChatHistory: React.Dispatch<React.SetStateAction<Message[]>>;
  chatHistory: Message[];
  setIsAiTyping: (isAiTyping: boolean) => void;
}> = ({
  message,
  selectOptions,
  invitePreview,
  placeRecommendations,
  setChatHistory,
  chatHistory,
  setIsAiTyping,
}) => {
  const { user } = useAuth();
  const [sendChatMessage] = useSendChatMessageMutation();
  const [copiedTimeId, setCopiedTimeId] = useState<string | null>(null);

  const handleSendMessage = async (message: string) => {
    if (!user) return;

    const userMessage = {
      author: 'user',
      message: message,
    };
    setChatHistory((prev) => [...prev, userMessage]);
    localStorage.setItem(
      'chatHistory',
      JSON.stringify([...chatHistory, userMessage]),
    );

    try {
      setIsAiTyping(true);
      const response = await sendChatMessage({
        body: message,
        from: user.phoneNumber,
      });

      if (response.data) {
        const aiMessage = {
          author: 'ai',
          message: response.data.response,
        };
        setChatHistory((prev) => [...prev, aiMessage]);
        localStorage.setItem(
          'chatHistory',
          JSON.stringify([...chatHistory, userMessage, aiMessage]),
        );
        setIsAiTyping(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setIsAiTyping(false);
    }
  };

  const handleCopyTime = async (time: string) => {
    const linkToCopy = time;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(linkToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = linkToCopy;
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedTimeId(time);
      setTimeout(() => setCopiedTimeId(null), 1000);
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  const formatMessage = (message: string) => {
    return message
      .replace(/(Location Preferences:)/g, '\n\n$1\n')
      .replace(/(https?:\/\/[^\s]+)/g, '$1\n')
      .replace(/\n{2,}/g, '\n\n')
      .replace(/(Reply with )/g, '\n$1')
      .replace(/(For example)/g, '\n$1')
      .replace(/(Do you want to change)/g, '\n$1')
      .trim();
  };

  const isSelectedTime = useMemo(() => {
    return message?.includes('finalize');
  }, [message]);

  return (
    <div className="flex items-start mb-5">
      <div className="mr-2 mt-1 flex-shrink-0">
        <ButterflyIcon className="w-6 h-6 text-[#D7C0FB]" />
      </div>
      <div className="flex flex-col max-w-[85%]">
        {message !== '' && (
          <div className="p-3 mb-3">
            <p
              className="text-white text-[0.875rem]"
              style={{ whiteSpace: 'pre-line' }}
            >
              {formatMessage(message)}
            </p>
          </div>
        )}
        <div className="flex flex-col gap-2">
          {selectOptions &&
            !invitePreview &&
            !isSelectedTime &&
            selectOptions.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="ml-3 text-white text-[0.875rem]">
                  Click to copy a suggested time:
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectOptions.map((option, index) => (
                    <button
                      key={index}
                      data-time={option.label}
                      className={cn(
                        'relative bg-[#6750A4] text-white rounded-full px-4 py-2 text-xs transition-colors duration-300',
                        ' hover:text-white/80',
                        copiedTimeId === option.label &&
                          'text-white/80 bg-[#4b3b76]',
                      )}
                      onClick={() => handleCopyTime(option.label)}
                    >
                      <span
                        className={cn(
                          'transition-all duration-300',
                          copiedTimeId === option.label
                            ? 'opacity-0'
                            : 'opacity-100',
                        )}
                      >
                        {option.label}
                      </span>

                      <span
                        className={cn(
                          'absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center transition-all duration-300',
                          copiedTimeId === option.label
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      >
                        Copied!
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          {placeRecommendations &&
            !invitePreview &&
            selectOptions &&
            selectOptions.length === 0 &&
            placeRecommendations.length >= 2 && (
              <div className="flex flex-col gap-2 w-full">
                {placeRecommendations.map((place, index) => (
                  <PlaceCard
                    key={index}
                    place={place}
                    variant="ai"
                    onClick={() => handleSendMessage(place.name)}
                  />
                ))}
              </div>
            )}
          {invitePreview && <InvitePreviewCard preview={invitePreview} />}
        </div>
      </div>
    </div>
  );
};

const Chat: React.FC<{
  messages: Message[];
  onSwipeDown: () => void;
  setChatHistory: React.Dispatch<React.SetStateAction<Message[]>>;
  chatHistory: Message[];
  setIsAiTyping: (isAiTyping: boolean) => void;
}> = ({
  messages,
  onSwipeDown,
  setChatHistory,
  chatHistory,
  setIsAiTyping,
}) => {
  const chatRef = useRef<HTMLDivElement>(null);
  const [sendChatMessage, { isLoading }] = useSendChatMessageMutation();

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="flex-1 overflow-y-auto p-4" ref={chatRef}>
      {messages.map((msg, index) => {
        return (
          <React.Fragment key={index}>
            {msg.author === 'ai' ? (
              <AIMessage
                message={msg.message}
                selectOptions={msg.selectOptions}
                invitePreview={msg.invitePreview}
                placeRecommendations={msg.placeRecommendations}
                setChatHistory={setChatHistory}
                chatHistory={chatHistory}
                setIsAiTyping={setIsAiTyping}
              />
            ) : (
              <UserMessage
                message={msg.message}
                selectedPlace={msg.selectedPlace}
              />
            )}
          </React.Fragment>
        );
      })}
      {messages.length === 0 && (
        <div className="w-full text-white text-xs text-center">
          Ask Calido AI anything about your day
        </div>
      )}
      {isLoading && <div className="text-white text-xs">Loading...</div>}
    </div>
  );
};

const ChatModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  buttonPosition: { top: number; left: number; width: number; height: number };
  message: string;
  setMessage: (message: string) => void;
  onClearChat: () => void;
  chatHistory: Message[];
  setChatHistory: React.Dispatch<React.SetStateAction<Message[]>>;
  selectedLocation: string | null;
  setSelectedLocation: (location: string | null) => void;
  isLocationPopupOpen: boolean;
  setIsLocationPopupOpen: (isOpen: boolean) => void;
  isAiTyping: boolean;
  setIsAiTyping: (isAiTyping: boolean) => void;
}> = ({
  isOpen,
  onClose,
  buttonPosition,
  message,
  setMessage,
  onClearChat,
  chatHistory,
  setChatHistory,
  selectedLocation,
  setSelectedLocation,
  isLocationPopupOpen,
  setIsLocationPopupOpen,
  isAiTyping,
  setIsAiTyping,
}) => {
  const { user } = useAuth();
  const [wasOpened, setWasOpened] = useState(false);
  const [sendChatMessage] = useSendChatMessageMutation();
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setWasOpened(true);
    }
  }, [isOpen]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  if (!user) return null;
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const animationName = `chatExpand-${buttonPosition.top}-${buttonPosition.left}`;
  const animationStyle = `
    @keyframes ${animationName} {
      0% {
        transform: scale(0);
        opacity: 0;
        top: ${buttonPosition.top}px;
        left: ${buttonPosition.left}px;
        width: ${buttonPosition.width}px;
        height: ${buttonPosition.height}px; 
        border-radius: 50px;
      }
      30% {
        opacity: 0.7;
      }
      100% {
        transform: scale(1);
        opacity: 1;
        top: 10.2svh;
        left: 0px;
        width: 100%;
        height: 90%;
        border-radius: 20px 20px 0 0;
      }
    }

    @keyframes ${animationName}-close {
      0% {
       transform: scale(1);
        opacity: 1;
        top: 10.2svh;
        left: 0px;
        width: 100%;
        height: 90%;
        border-radius: 20px 20px 0 0;
      }
      70% {
        opacity: 0.7;
      }
      100% {
        transform: scale(0);
        opacity: 0; 
        top: ${buttonPosition.top}px;
        left: ${buttonPosition.left}px;
        width: ${buttonPosition.width}px;
        height: ${buttonPosition.height}px;
        border-radius: 0;
      }
    }
  `;

  const handleSendMessage = async (message: string) => {
    if (!user) return;
    try {
      setIsAiTyping(true);
      const response = await sendChatMessage({
        body: message,
        from: user.phoneNumber,
      });

      if (response.data) {
        const updatedChatHistory = [
          ...chatHistory,
          {
            author: 'user',
            message: message,
          },
          {
            author: 'ai',
            message: response.data.response,
          },
        ];
        setChatHistory(updatedChatHistory);
        localStorage.setItem('chatHistory', JSON.stringify(updatedChatHistory));
        setIsAiTyping(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setIsAiTyping(false);
    }
  };
  const parseSelectOptions = (message: string): SelectOption[] => {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const getDaySuffix = (day: number) => {
      if (day >= 11 && day <= 13) return 'th';
      const lastDigit = day % 10;
      if (lastDigit === 1) return 'st';
      if (lastDigit === 2) return 'nd';
      if (lastDigit === 3) return 'rd';
      return 'th';
    };

    const timeRegex = new RegExp(
      `(?:(\\d{2})\\/(\\d{2}) (\\d{1,2}):(\\d{2}) ([AP]\\.?M\\.?)|` +
        `(?:(${months.join('|')}) (\\d{1,2}) at (\\d{1,2}):(\\d{2}) ([AP]\\.?M\\.?)))`,
      'gi',
    );

    const matches = Array.from(message.matchAll(timeRegex));

    const formattedTimes = matches
      .map((match) => {
        if (match[1]) {
          const [, month, day, hour, minute, ampm] = match;
          const monthName = months[parseInt(month, 10) - 1];
          const dayNum = parseInt(day, 10);
          const suffix = getDaySuffix(dayNum);
          return `${monthName} ${dayNum}${suffix} at ${hour}:${minute}${ampm.toLowerCase().replace('.', '')}`;
        } else if (match[6]) {
          const [, , , monthName, day, hour, minute, ampm] = match;
          const dayNum = parseInt(day, 10);
          const suffix = getDaySuffix(dayNum);
          return `${monthName} ${dayNum}${suffix} at ${hour}:${minute}${ampm.toLowerCase().replace('.', '')}`;
        }
        return '';
      })
      .filter(Boolean);

    return formattedTimes.map((time, index) => ({
      label: time,
      value: `time_${index + 1}`,
    }));
  };

  const parsePlaceRecommendations = (
    message: string,
  ): PlaceRecommendation[] => {
    const cleanMessage = message.replace(/.*\s+location to\s+/i, '').trim();

    const placeRegex = /([A-Za-z\s']+) - (https?:\/\/[^\s]+)/g;
    const places: PlaceRecommendation[] = [];

    let match;
    while ((match = placeRegex.exec(cleanMessage)) !== null) {
      places.push({
        name: match[1].trim(),
      });
    }

    return places;
  };

  const parseInvitePreview = (message: string): InvitePreview | null => {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const getDaySuffix = (day: number) => {
      if (day >= 11 && day <= 13) return 'th';
      const lastDigit = day % 10;
      if (lastDigit === 1) return 'st';
      if (lastDigit === 2) return 'nd';
      if (lastDigit === 3) return 'rd';
      return 'th';
    };
    const eventRegex = /🎉 Event \((.*?)\) "(.*?)" has been finalized! 🎉/;
    const dateTimeRegex = /📅 Time:\s*([\d/]+)\s+([\d:]+)\s*([APM.]+),\s*(\w+)/;
    const locationRegex = /📍 Location:\s*(.*?)(?=\s*url:|$)/;
    const urlRegex = /url:\s*(https?:\/\/[^\s]+)/;

    const eventMatch = message.match(eventRegex);
    const dateTimeMatch = message.match(dateTimeRegex);
    const locationMatch = message.match(locationRegex);
    const urlMatch = message.match(urlRegex);

    if (!eventMatch || !dateTimeMatch) return null;

    const location = locationMatch?.[1].split(' - ').map((word) => word.trim());

    const date = dateTimeMatch[1]; // MM/DD
    let time = dateTimeMatch[2]; // hh:mm A.M/P.M
    const period = dateTimeMatch[3]; // AM/PM

    // Format time to 6:00PM
    time = `${time}${period.replace('.', '')}`;

    const month = months[parseInt(date.split('/')[0], 10) - 1];
    const day = date.split('/')[1];
    const suffix = getDaySuffix(parseInt(day, 10));
    const formattedDate = `${month} ${day}${suffix}`;

    return {
      title: eventMatch[2] || '',
      date: formattedDate,
      time,
      location: location ? location[0] : '',
      link: urlMatch ? urlMatch[1].trim() : '',
    };
  };

  const parseMessages = (chatHistory: any[]): Message[] => {
    return chatHistory.map((msg) => {
      const placeRecommendationsMessage = parsePlaceRecommendations(
        msg.message,
      );
      const selectOptionsMessage = parseSelectOptions(msg.message);
      const invitePreviewMessage = parseInvitePreview(msg.message);

      let filteredMessage = msg.message;

      filteredMessage = filteredMessage
        .replace(
          /(?:Time Preferences:)|\d{2}\/\d{2} \d{1,2}:\d{2} [AP]\.?M\.?|(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)|\b\d+\.\s+/gi,
          '',
        )
        .trim();

      // filteredMessage = filteredMessage
      //   .replace(/(?:Location Preferences:)|\d+\.\s*/gi, '')
      //   .trim();

      // filteredMessage = filteredMessage
      //   .replace(/Location Preferences:(?:\s*[\w\s-]+ - https?:\/\/\S+)+/g, '')
      //   .trim();

      if (invitePreviewMessage) {
        filteredMessage = filteredMessage
          .replace(
            /🎉 Event \((.*?)\) ".*?" has been finalized! 🎉|📅 Time:\s*(?:[\d/]+\s+[\d:]+\s*[APM.]+,?\s*\w*)?|📅 Time:\s*,?|📍 Location:\s*.*?(?=\s*url:|$)|url:\s*(https?:\/\/[^\s]+)/gi,
            '',
          )
          .replace(/,\s*$/, '')
          .trim();
      }

      // filteredMessage = filteredMessage.replace(
      //   /([A-Za-z\s]+) - (https?:\/\/[^\s]+)/g,
      //   '',
      // );
      filteredMessage = filteredMessage.replace(
        /I've changed the event location to .*? - https?:\/\/[^\s]+(?:\s*and time to .*?)?\./g,
        '',
      );

      filteredMessage = filteredMessage
        .replace(/\s{2,}/g, ' ')
        .replace(/,(\s*,)+/g, ',')
        .replace(/,\s+\./g, '.')
        .replace(/ , /g, '. ')
        .trim();

      const timeMatch = msg.message.match(
        /\d{2}\/\d{2} \d{1,2}:\d{2} [AP]\.?M\.?/,
      );
      const suggestedTime = filteredMessage?.includes('Based on your calendar');

      if (timeMatch && !invitePreviewMessage && !suggestedTime) {
        const time = timeMatch[0];
        filteredMessage += `\n\n⏰ Selected Time: ${time}`;
      }

      if (placeRecommendationsMessage.length === 1 && !invitePreviewMessage) {
        const location = placeRecommendationsMessage[0].name;
        filteredMessage += ` 📍 Selected Location: ${location}`;
      }
      filteredMessage = filteredMessage.replace(/I' and time to ./i, '').trim();

      const parsedMessage: Message = {
        author: msg.author,
        message: filteredMessage,
        selectOptions: selectOptionsMessage,
        invitePreview: invitePreviewMessage || undefined,
        placeRecommendations: placeRecommendationsMessage,
        selectedPlace: msg.selectedPlace || '',
      };

      return parsedMessage;
    });
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50"
      style={{
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
      <style>{animationStyle}</style>
      <div className="absolute inset-0" onClick={onClose} />
      <div
        className={`bg-[#2A2B4A] absolute rounded-t-[20px] shadow-lg overflow-hidden flex flex-col chat-modal`}
        style={{
          animation: isOpen
            ? `${animationName} 450ms cubic-bezier(0.17, 0.67, 0.27, 1.05) forwards`
            : wasOpened
              ? `${animationName}-close 450ms cubic-bezier(0.17, 0.67, 0.27, 1.05) forwards`
              : 'none',
          boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.6)',
          height: isOpen
            ? `${Math.min(90, (viewportHeight / window.innerHeight) * 100)}vh`
            : '0',
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          transform: 'translateY(0)',
          opacity: '1',
          transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
        }}
      >
        <div className="relative">
          <div
            className="flex justify-between items-center p-3 border-b border-[#353555]/50 cursor-grab active:cursor-grabbing"
            onTouchStart={(e) => {
              const touch = e.touches[0];
              const startY = touch.clientY;
              const header = e.currentTarget as HTMLElement;

              const handleTouchMove = (e: TouchEvent) => {
                const touch = e.touches[0];
                const currentY = touch.clientY;
                const deltaY = currentY - startY;

                if (deltaY > 0) {
                  e.preventDefault();
                  const progress = Math.min(deltaY / 100, 1);
                  const modal = header.closest('.chat-modal') as HTMLElement;
                  if (modal) {
                    modal.style.setProperty(
                      'transform',
                      `translateY(${deltaY}px)`,
                    );
                    modal.style.setProperty('opacity', `${1 - progress}`);
                  }
                }
              };

              const handleTouchEnd = (e: TouchEvent) => {
                const touch = e.changedTouches[0];
                const deltaY = touch.clientY - startY;

                if (deltaY > 100) {
                  onClose();
                } else {
                  const modal = header.closest('.chat-modal') as HTMLElement;
                  if (modal) {
                    modal.style.setProperty('transform', 'translateY(0)');
                    modal.style.setProperty('opacity', '1');
                  }
                }

                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
              };

              document.addEventListener('touchmove', handleTouchMove);
              document.addEventListener('touchend', handleTouchEnd);
            }}
          >
            <div className="flex items-center">
              <div
                className="bg-[#6750A4]/30 text-white rounded-full px-3 py-1 flex items-center"
                onClick={() => setIsLocationPopupOpen(true)}
              >
                <MapPin className="w-3 h-3 mr-1 text-[#D7C0FB]" />
                <span className="text-[10px] text-white">
                  {selectedLocation}
                </span>
              </div>
              <h2 className="ml-3 text-xl font-medium text-[#D7C0FB]">
                CALIDO<sub className="text-[#D7C0FB]">AI</sub>
              </h2>
            </div>
            <button
              className="text-white/70 text-sm p-2 hover:bg-[#353555]/50 rounded-full transition-colors"
              onClick={onClearChat}
            >
              Clear chat
            </button>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 top-0 w-16 h-1 bg-[#353555]/50 rounded-full mt-1"></div>
        </div>

        <Chat
          messages={parseMessages(chatHistory)}
          onSwipeDown={onClose}
          setChatHistory={setChatHistory}
          chatHistory={chatHistory}
          setIsAiTyping={setIsAiTyping}
        />
        {isAiTyping && (
          <div className="flex justify-center items-center p-3">
            <div className="text-white text-xs animate-pulse">
              Calido is typing...
            </div>
          </div>
        )}

        <div className="p-3">
          <div className="relative flex justify-center items-end max-h-[30vh] h-full">
            <div className="absolute left-2 bottom-6">
              <EmojiAvatar
                fullName={user.name}
                image={user.photoURL}
                size="small"
                className="w-8 h-8"
              />
            </div>
            <div className="flex justify-center items-center w-full h-full overflow-y-scroll">
              <textarea
                ref={textareaRef}
                value={message}
                onInput={handleInput}
                placeholder="Enter a comment..."
                className="w-full !fit bg-[#32395E] text-white rounded-xl py-3 px-12 focus:outline-none focus:ring-1 focus:ring-[#D7C0FB]/50"
                style={{
                  boxShadow: '0px 4px 10px 0px #0000000F',
                  resize: 'none',
                }}
                autoFocus={isOpen}
                aria-label="Message input"
              />
            </div>
            <button
              className="absolute right-4 bottom-6 text-[#D7C0FB]"
              onClick={() => {
                const updatedChatHistory = [
                  ...chatHistory,
                  {
                    author: 'user',
                    message: message,
                  },
                ];
                setChatHistory(updatedChatHistory);
                localStorage.setItem(
                  'chatHistory',
                  JSON.stringify(updatedChatHistory),
                );
                setMessage('');
                handleSendMessage(message);
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22 2L11 13"
                  stroke="#ABE49C"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 2L15 22L11 13L2 9L22 2Z"
                  stroke="#ABE49C"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <LocationPopup
        isOpen={isLocationPopupOpen}
        setIsOpen={setIsLocationPopupOpen}
        setSelectedLocation={setSelectedLocation}
        selectedLocation={selectedLocation}
      />
    </div>
  );
};

const LocationPopup: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setSelectedLocation: (location: string | null) => void;
  selectedLocation: string | null;
}> = ({ isOpen, setIsOpen, setSelectedLocation, selectedLocation }) => {
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);

  useEffect(() => {
    const loadScript = (url: string, callback: () => void) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = callback;
      document.body.appendChild(script);
    };

    loadScript(
      `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`,
      () => {
        autocompleteServiceRef.current =
          new window.google.maps.places.AutocompleteService();
      },
    );
  }, []);

  const handleLocationChange = useCallback((value: string) => {
    if (value) {
      autocompleteServiceRef.current?.getPlacePredictions(
        { input: value, types: ['establishment', 'geocode'] },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions((prev) => {
              const newItems = predictions.filter(
                (prediction) =>
                  !prev.some((item) => item.place_id === prediction.place_id),
              );

              return [...prev, ...newItems] as PlacePrediction[];
            });
          } else {
            setSuggestions((prev) => {
              const newItems = predictions.filter(
                (prediction) =>
                  !prev.some((item) => item.place_id === prediction.place_id),
              );

              return [...prev, ...newItems] as PlacePrediction[];
            });
          }
        },
      );
    } else {
      return;
    }
  }, []);

  const onLocationChange = (location: string) => {
    setSelectedLocation(location);
  };

  return (
    <div
      className={`w-full flex flex-col justify-end items-center pointer-events-none fixed inset-0 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
    >
      {isOpen && (
        <div
          className="absolute inset-0 bg-[#0A0118] opacity-70 pointer-events-auto"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div
        className={`w-full h-[56svh] flex flex-col justify-between items-center relative z-10 p-5 pb-[28svh] bg-[#0A0118] rounded-[1rem] ${
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
      >
        <div className="text-white text-center text-[1.3rem] font-semibold">
          <div className="flex flex-col gap-2">
            Update your location
            <div className="text-white/80 text-center text-[0.8rem] font-normal">
              This will help us find the best places for you
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <SquareAutocompleteInput
            placeholder="Enter location..."
            aria-label="Location input"
            defaultEmoji="📍"
            label="Location"
            dropdownLabel="Locations"
            dropdownEmptyLabel="No locations found"
            editInputValue={selectedLocation || undefined}
            onInputValueChange={handleLocationChange}
            options={suggestions.map((suggestion) => ({
              label:
                suggestion?.structured_formatting?.main_text ||
                suggestion.description,
              value: suggestion.place_id,
              subLabel: suggestion.structured_formatting.secondary_text,
            }))}
            selectedOption={selectedLocation}
            setSelectedOption={(option) => {
              setSelectedLocation(option);
              if (option) {
                const selectedSuggestion = suggestions.find(
                  (s) => s.place_id === option,
                );
                if (selectedSuggestion) {
                  onLocationChange(selectedSuggestion.description);
                }
              }
            }}
          />
        </div>
        <div className="flex flex-row gap-4">
          <Button
            variant="secondary"
            className="w-full px-6 py-3"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="w-full px-6 py-3"
            onClick={() => {
              setIsOpen(false);
              if (selectedLocation) {
                localStorage.setItem('location', selectedLocation);
              }
            }}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

type Props = {
  isShadows?: boolean;
};

const HomeAICard: React.FC<Props> = ({ isShadows = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAiTyping, setIsAiTyping] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(
    localStorage.getItem('location') || null,
  );
  const [isLocationPopupOpen, setIsLocationPopupOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const [message, setMessage] = useState('');
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const [chatHistory, setChatHistory] = useState<Message[]>(() => {
    const savedChatHistory = localStorage.getItem('chatHistory');
    return savedChatHistory ? JSON.parse(savedChatHistory) : [];
  });
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    let container = document.getElementById('chat-portal-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'chat-portal-container';
      document.body.appendChild(container);
    }
    setPortalContainer(container);

    return () => {
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };
  }, []);

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  }, []);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      @keyframes fadeIn {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleToggleChat = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
    setIsExpanded((prev) => !prev);
  };

  const handleCloseChat = () => {
    setIsExpanded(false);
  };

  const handleClearChat = () => {
    setChatHistory([]);
    localStorage.removeItem('chatHistory');
  };

  return (
    <div className="flex justify-center items-center">
      {isShadows && (
        <div
          className="absolute bg-transparent w-full shadow-[0px_50px_110px_-61px_#AAA8FF] rounded-[6rem] z-[-1]"
          style={{ height: '100%' }}
        ></div>
      )}
      <div
        className="rounded-[16px] px-[20px] py-[16px] bg-[#2A2B4A]/90 max-h-[20rem] w-full h-full relative"
        style={{
          boxShadow: '0px 0px 5px 0px rgba(255, 255, 255, 0.56)',
          backdropFilter: 'blur(5px)',
          zIndex: 2,
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-[20px] font-semibold text-[#D7C0FB] flex-shrink-0">
            What's happening?
          </h1>
          <div
            className="bg-[#6750A4]/30 text-white rounded-full px-4 py-2 flex items-center min-w-0"
            onClick={() => setIsLocationPopupOpen(true)}
          >
            <MapPin className="w-4 h-4 mr-1 text-[#D7C0FB] flex-shrink-0" />
            <span className="text-[10px] truncate">
              {selectedLocation || 'Select location'}
            </span>
          </div>
        </div>

        <button
          ref={buttonRef}
          className="w-full bg-[#5D5D6F]/70 rounded-full py-3 px-4 flex items-center mb-4"
          style={{
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset',
          }}
          onClick={handleToggleChat}
          aria-expanded={isExpanded}
          aria-haspopup="dialog"
        >
          <ButterflyIcon className="w-6 h-6 mr-3" />
          <span className="text-base text-white font-medium">
            Ask Calido AI
          </span>
        </button>

        <p className="text-white text-center mb-4 text-sm">
          Click icon above or prompts below to ask Calido
        </p>

        <div className="max-w-md w-full  flex -m-1 justify-normal">
          <div
            className="flex -m-1 justify-normal overflow-x-scroll"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
            }}
          >
            {mockPrompts.map((prompt) => (
              <PromptButton
                key={prompt.id}
                prompt={prompt}
                isOpen={isExpanded}
                setIsOpen={setIsExpanded}
                chatHistory={chatHistory}
                setChatHistory={setChatHistory}
                setIsAiTyping={setIsAiTyping}
              />
            ))}
          </div>
        </div>

        {portalContainer &&
          ReactDOM.createPortal(
            <ChatModal
              isOpen={isExpanded}
              onClose={handleCloseChat}
              buttonPosition={buttonPosition}
              message={message}
              setMessage={setMessage}
              onClearChat={handleClearChat}
              chatHistory={chatHistory}
              setChatHistory={setChatHistory}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              isLocationPopupOpen={isLocationPopupOpen}
              setIsLocationPopupOpen={setIsLocationPopupOpen}
              isAiTyping={isAiTyping}
              setIsAiTyping={setIsAiTyping}
            />,
            portalContainer,
          )}
      </div>
    </div>
  );
};

export default HomeAICard;
